import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FeedbackForm from './components/FeedbackForm';

const highlights = [
  {
    icon: 'ri-film-line',
    title: '丰富影视资源',
    desc: '整合电影与电视剧内容，按地区、年份与类型精准筛选，快速定位你想看的作品。',
  },
  {
    icon: 'ri-apple-fill',
    title: '苹果产品视频',
    desc: '聚焦新品发布、专业评测与使用教程，帮助数码爱好者了解产品真实表现。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '便捷浏览体验',
    desc: '支持全站关键词搜索、分类筛选与排序，配合收藏与分享，提升观看与信息获取效率。',
  },
];

const contactItems = [
  { icon: 'ri-mail-line', label: '邮箱', value: 'contact@apple-video.com' },
  { icon: 'ri-phone-line', label: '客服热线', value: '400-000-0000' },
  { icon: 'ri-map-pin-line', label: '办公地址', value: '中国·上海市浦东新区科创大道 1 号' },
  { icon: 'ri-time-line', label: '服务时间', value: '周一至周五 09:00 - 18:00' },
];

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于我们 - 苹果视频平台介绍与联系方式';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />

      <main className="flex-1">
        <section className="bg-secondary-900 px-4 py-14 md:px-8 md:py-20">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
          <div className="mt-6 max-w-3xl">
            <h1 className="font-heading text-3xl font-black text-background-50 md:text-5xl">
              关于苹果视频
            </h1>
            <p className="mt-5 text-sm leading-loose text-background-300 md:text-base">
              苹果视频是一个面向影视视频爱好者与苹果产品爱好者的内容展示平台。我们专注于整合优质影视资源与苹果品牌相关视频内容，涵盖电影、电视剧，以及苹果新品介绍、评测与使用教程等多个领域。平台希望通过清晰的内容分类、便捷的筛选与搜索，以及舒适的浏览体验，让每一位用户都能高效地找到自己感兴趣的视频内容。
            </p>
          </div>
        </section>

        <section className="px-4 py-12 md:px-8 md:py-16" aria-label="平台特色">
          <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
            平台特色
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-6">
            {highlights.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-background-200/80 bg-background-50 p-6"
              >
                <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <i className={`${item.icon} text-2xl`} aria-hidden="true" />
                </span>
                <h3 className="mb-2 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="text-sm leading-relaxed text-foreground-600">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-50 px-4 py-12 md:px-8 md:py-16" aria-label="版权声明与联系方式">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                版权声明
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-600">
                <p>
                  本站为演示性质的内容展示平台，站内所展示的影视、产品及品牌资料均为示例内容，仅用于界面与交互效果展示，不提供任何真实视频播放源。
                </p>
                <p>
                  涉及第三方品牌名称、商标及产品信息，其相关权利均归各自权利人所有。本站不承接影视作品的在线播放业务，也不存储任何影视资源文件。
                </p>
                <p>
                  如权利人认为站内示例内容存在不当之处，可通过下方联系方式与我们取得联系，我们将及时核实处理。
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {contactItems.map((item) => (
                  <div
                    key={item.label}
                    className="flex items-start gap-3 rounded-lg border border-background-200/80 bg-background-100 p-4"
                  >
                    <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                      <i className={`${item.icon} text-lg`} aria-hidden="true" />
                    </span>
                    <div>
                      <p className="text-xs text-foreground-500">{item.label}</p>
                      <p className="mt-0.5 text-sm font-medium text-foreground-800">
                        {item.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <FeedbackForm />
            </div>
          </div>
        </section>

        <section className="px-4 pb-16 md:px-8" aria-label="返回导航">
          <div className="flex flex-col items-center justify-between gap-4 rounded-lg border border-background-200/80 bg-background-50 p-6 text-center sm:flex-row sm:text-left">
            <div>
              <h3 className="font-heading text-base font-bold text-foreground-950">
                回到首页，发现更多精彩内容
              </h3>
              <p className="mt-1 text-sm text-foreground-600">
                浏览最新影视推荐与苹果产品视频精选。
              </p>
            </div>
            <Link
              to="/"
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-home-4-line text-lg" aria-hidden="true" />
              返回首页
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}